<template>
  <div class="about">
    <!-- 动态渲染所有配置的区块 -->
    <component 
      v-for="section in pageConfig.sections" 
      :key="section.id"
      :is="getSectionComponent(section.type)"
      :section="section"
      v-show="section.enabled !== false"
    />
  </div>
</template>

<script>
// 导入所有可用的区块组件
import PageHero from './sections/PageHero.vue'
import ContentIntro from './sections/ContentIntro.vue'
import GridCards from './sections/GridCards.vue'
import TeamGrid from './sections/TeamGrid.vue'
import Timeline from './sections/Timeline.vue'
import StatsSection from './sections/StatsSection.vue'
import TestimonialsSection from './sections/TestimonialsSection.vue'
import ContactCTA from './sections/ContactCTA.vue'

export default {
  name: 'About',
  components: {
    PageHero,
    ContentIntro,
    GridCards,
    TeamGrid,
    Timeline,
    StatsSection,
    TestimonialsSection,
    ContactCTA
  },
  
  data () {
    return {
      // 完全灵活的页面配置
      pageConfig: {
        // 页面基础信息
        meta: {
          title: '关于我们',
          description: '了解我们的团队、文化和发展历程'
        },
        
        // 动态区块配置 - 可以任意增减、重排序、选择不同组件类型
        sections: [
          {
            id: 'hero',
            type: 'hero',
            enabled: true,
            order: 1,
            config: {
              title: '关于我们',
              subtitle: '致力于为企业提供最优质的数字化解决方案',
              backgroundType: 'gradient', // gradient, image, solid
              backgroundConfig: {
                colors: ['var(--primary-color)', 'var(--primary-dark)'],
                direction: '135deg'
              },
              style: {
                padding: '120px 0 80px',
                textAlign: 'center',
                color: 'white'
              }
            }
          },
          
          {
            id: 'company-intro',
            type: 'content-intro',
            enabled: true,
            order: 2,
            config: {
              layout: 'split', // split, centered, full-width
              title: '我们的使命',
              content: [
                '自成立以来，我们始终专注于为企业提供创新的数字化解决方案。我们相信技术的力量能够改变世界，通过不断的创新和优质的服务，帮助客户在数字化时代取得成功。',
                '我们的团队由经验丰富的技术专家和业务顾问组成，拥有深厚的行业知识和丰富的项目经验。我们致力于理解客户的业务需求，提供量身定制的解决方案，确保每个项目的成功交付。'
              ],
              features: [
                {
                  id: 1,
                  title: '创新驱动',
                  description: '持续投入研发，保持技术领先',
                  icon: '💡'
                },
                {
                  id: 2,
                  title: '客户至上',
                  description: '以客户需求为导向，提供优质服务',
                  icon: '🎯'
                },
                {
                  id: 3,
                  title: '团队协作',
                  description: '发挥团队智慧，共创美好未来',
                  icon: '🤝'
                }
              ],
              mediaConfig: {
                type: 'icon', // icon, image, video
                content: '🏢',
                position: 'right' // left, right, top, bottom
              },
              style: {
                backgroundColor: 'white',
                padding: '80px 0'
              }
            }
          },

          {
            id: 'company-stats',
            type: 'stats',
            enabled: true,
            order: 3,
            config: {
              layout: 'horizontal', // horizontal, grid, vertical
              title: '数据说话',
              subtitle: '用数字见证我们的成长',
              stats: [
                {
                  id: 1,
                  number: '500+',
                  label: '服务客户',
                  icon: '👥',
                  color: 'var(--primary-color)'
                },
                {
                  id: 2,
                  number: '1000+',
                  label: '成功项目',
                  icon: '🚀',
                  color: 'var(--success-color)'
                },
                {
                  id: 3,
                  number: '5年',
                  label: '行业经验',
                  icon: '⭐',
                  color: 'var(--warning-color)'
                },
                {
                  id: 4,
                  number: '99%',
                  label: '客户满意度',
                  icon: '❤️',
                  color: 'var(--danger-color)'
                }
              ],
              style: {
                backgroundColor: 'var(--gray-50)',
                padding: '60px 0'
              }
            }
          },
          
          {
            id: 'culture',
            type: 'grid-cards',
            enabled: true,
            order: 4,
            config: {
              title: '企业文化',
              subtitle: '我们的核心价值观指引着我们前进的方向',
              layout: 'grid', // grid, masonry, carousel
              columns: 4, // 1-6
              cardStyle: 'elevated', // flat, elevated, outlined
              items: [
                {
                  id: 1,
                  icon: '💡',
                  title: '创新精神',
                  description: '鼓励创新思维，勇于尝试新技术和新方法，持续改进和优化。',
                  color: 'var(--primary-color)'
                },
                {
                  id: 2,
                  icon: '🤝',
                  title: '诚信合作',
                  description: '以诚待人，诚信经营，与客户和合作伙伴建立长期信任关系。',
                  color: 'var(--success-color)'
                },
                {
                  id: 3,
                  icon: '🎯',
                  title: '追求卓越',
                  description: '精益求精，不断提升产品质量和服务水平，追求完美。',
                  color: 'var(--warning-color)'
                },
                {
                  id: 4,
                  icon: '🌱',
                  title: '持续学习',
                  description: '保持学习热情，紧跟技术发展趋势，提升专业能力。',
                  color: 'var(--info-color)'
                }
              ],
              style: {
                backgroundColor: 'white',
                padding: '80px 0'
              }
            }
          },
          
          {
            id: 'team',
            type: 'team-grid',
            enabled: true,
            order: 5,
            config: {
              title: '核心团队',
              subtitle: '优秀的团队是我们成功的基础',
              layout: 'grid', // grid, list, carousel
              columns: 4,
              showSocial: true,
              members: [
                {
                  id: 1,
                  name: '张伟',
                  position: '首席执行官',
                  avatar: '👨‍💼',
                  description: '10年企业管理经验，曾任职于多家知名科技公司，专注于企业数字化转型。',
                  social: {
                    linkedin: '#',
                    email: 'zhang.wei@company.com'
                  }
                },
                {
                  id: 2,
                  name: '李梅',
                  position: '技术总监',
                  avatar: '👩‍💻',
                  description: '资深技术专家，精通多种编程语言和架构设计，领导技术团队创新发展。',
                  social: {
                    github: '#',
                    email: 'li.mei@company.com'
                  }
                },
                {
                  id: 3,
                  name: '王强',
                  position: '产品经理',
                  avatar: '👨‍💻',
                  description: '丰富的产品设计经验，深度理解用户需求，打造优秀的产品体验。',
                  social: {
                    twitter: '#',
                    email: 'wang.qiang@company.com'
                  }
                },
                {
                  id: 4,
                  name: '赵丽',
                  position: '市场总监',
                  avatar: '👩‍💼',
                  description: '市场营销专家，具有敏锐的市场洞察力，成功推广多个项目。',
                  social: {
                    linkedin: '#',
                    email: 'zhao.li@company.com'
                  }
                }
              ],
              style: {
                backgroundColor: 'var(--gray-50)',
                padding: '80px 0'
              }
            }
          },
          
          {
            id: 'timeline',
            type: 'timeline',
            enabled: true,
            order: 6,
            config: {
              title: '发展历程',
              subtitle: '见证我们一路走来的重要里程碑',
              layout: 'alternating', // alternating, left, right, vertical
              items: [
                {
                  id: 1,
                  year: '2019',
                  title: '公司成立',
                  description: '公司正式成立，专注于为中小企业提供数字化解决方案。',
                  icon: '🏢',
                  type: 'milestone'
                },
                {
                  id: 2,
                  year: '2020',
                  title: '产品发布',
                  description: '发布第一款企业管理系统，获得市场积极反馈。',
                  icon: '🚀',
                  type: 'product'
                },
                {
                  id: 3,
                  year: '2021',
                  title: '团队扩张',
                  description: '团队规模扩大到50人，建立了完善的研发体系。',
                  icon: '👥',
                  type: 'growth'
                },
                {
                  id: 4,
                  year: '2022',
                  title: '业务拓展',
                  description: '业务覆盖全国多个城市，服务客户超过500家。',
                  icon: '🌏',
                  type: 'expansion'
                },
                {
                  id: 5,
                  year: '2023',
                  title: '技术创新',
                  description: '引入AI技术，推出智能化产品线，获得多项技术专利。',
                  icon: '🤖',
                  type: 'innovation'
                },
                {
                  id: 6,
                  year: '2024',
                  title: '战略升级',
                  description: '启动数字化转型3.0战略，致力于成为行业领军企业。',
                  icon: '⚡',
                  type: 'strategy'
                }
              ],
              style: {
                backgroundColor: 'white',
                padding: '80px 0'
              }
            }
          },

          {
            id: 'testimonials',
            type: 'testimonials',
            enabled: true,
            order: 7,
            config: {
              title: '客户评价',
              subtitle: '听听客户对我们的评价',
              layout: 'carousel', // grid, carousel, masonry
              autoplay: true,
              testimonials: [
                {
                  id: 1,
                  content: '合作非常愉快，团队专业度很高，项目交付及时，质量超出预期。',
                  author: '李总',
                  company: '某科技公司',
                  avatar: '👨‍💼',
                  rating: 5
                },
                {
                  id: 2,
                  content: '技术实力强，服务态度好，是值得信赖的合作伙伴。',
                  author: '王经理',
                  company: '某制造企业',
                  avatar: '👩‍💼',
                  rating: 5
                },
                {
                  id: 3,
                  content: '解决方案很有创新性，帮助我们大大提升了工作效率。',
                  author: '张主任',
                  company: '某互联网公司',
                  avatar: '👨‍💻',
                  rating: 5
                }
              ],
              style: {
                backgroundColor: 'var(--gray-50)',
                padding: '80px 0'
              }
            }
          },

          {
            id: 'contact-cta',
            type: 'contact-cta',
            enabled: true,
            order: 8,
            config: {
              title: '开始合作',
              subtitle: '让我们一起创造更美好的未来',
              description: '无论您是需要数字化转型咨询，还是寻找技术解决方案，我们都随时为您服务。',
              primaryButton: {
                text: '立即咨询',
                link: '/contact',
                style: 'primary'
              },
              secondaryButton: {
                text: '了解更多',
                link: '/services',
                style: 'outline'
              },
              style: {
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                padding: '80px 0'
              }
            }
          }
        ]
      }
    }
  },

  // 生命周期：模拟从CMS/API获取页面配置
  async created () {
    console.log('🚀 开始加载关于我们页面配置...')
    
    // 模拟API调用延迟
    await this.loadPageConfig()
    
    console.log('✅ 页面配置加载完成:', this.pageConfig)
  },

  methods: {
    // 根据区块类型返回对应的组件名
    getSectionComponent (type) {
      const componentMap = {
        'hero': 'PageHero',
        'content-intro': 'ContentIntro',
        'grid-cards': 'GridCards',
        'team-grid': 'TeamGrid',
        'timeline': 'Timeline',
        'stats': 'StatsSection',
        'testimonials': 'TestimonialsSection',
        'contact-cta': 'ContactCTA'
      }
      
      return componentMap[type] || 'div'
    },

    // 模拟从API/CMS加载页面配置
    async loadPageConfig () {
      // 这里可以调用真实的API
      // const response = await api.getPageConfig('about')
      // this.pageConfig = response.data
      
      // 模拟网络延迟
      return new Promise(resolve => {
        setTimeout(() => {
          console.log('📊 页面数据加载完成')
          resolve()
        }, 300)
      })
    },

    // 动态更新页面配置（用于CMS管理后台）
    updatePageConfig (newConfig) {
      this.pageConfig = {
        ...this.pageConfig,
        ...newConfig
      }
      console.log('🔄 页面配置已更新:', this.pageConfig)
    },

    // 添加新区块
    addSection (sectionConfig) {
      const newSection = {
        id: 'section_' + Date.now(),
        order: this.pageConfig.sections.length + 1,
        enabled: true,
        ...sectionConfig
      }
      
      this.pageConfig.sections.push(newSection)
      this.sortSections()
      console.log('➕ 已添加新区块:', newSection)
    },

    // 删除区块
    removeSection (sectionId) {
      this.pageConfig.sections = this.pageConfig.sections.filter(
        section => section.id !== sectionId
      )
      console.log('❌ 已删除区块:', sectionId)
    },

    // 启用/禁用区块
    toggleSection (sectionId, enabled) {
      const section = this.pageConfig.sections.find(s => s.id === sectionId)
      if (section) {
        section.enabled = enabled
        console.log('🔄 区块 ' + sectionId + ' ' + (enabled ? '已启用' : '已禁用'))
      }
    },

    // 重新排序区块
    reorderSection (sectionId, newOrder) {
      const section = this.pageConfig.sections.find(s => s.id === sectionId)
      if (section) {
        section.order = newOrder
        this.sortSections()
        console.log('📋 区块顺序已更新')
      }
    },

    // 按order字段排序区块
    sortSections () {
      this.pageConfig.sections.sort((a, b) => a.order - b.order)
    },

    // 导出页面配置（用于备份或迁移）
    exportConfig () {
      const config = JSON.stringify(this.pageConfig, null, 2)
      console.log('📄 页面配置导出:', config)
      return config
    },

    // 导入页面配置
    importConfig (configJson) {
      try {
        const config = JSON.parse(configJson)
        this.pageConfig = config
        console.log('📥 页面配置导入成功')
      } catch (error) {
        console.error('❌ 配置导入失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.about {
  min-height: 100vh;
}

/* 全局区块样式 */
.about :deep(.section) {
  position: relative;
  overflow: hidden;
}

.about :deep(.container) {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.about :deep(.section-title) {
  text-align: center;
  margin-bottom: 3rem;
}

.about :deep(.section-title h2) {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
}

.about :deep(.section-title p) {
  font-size: 1.125rem;
  color: var(--gray-600);
  max-width: 600px;
  margin: 0 auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .about :deep(.section-title h2) {
    font-size: 2rem;
  }
  
  .about :deep(.section-title p) {
    font-size: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/About.vue